import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { Box, capitalize, IconButton, Paper, Typography } from "@mui/material";
import { styled } from "@mui/system";
import { useEffect, useState } from "react";

import useAPI from "../../../../../../api/hooks/useAPI";
import useColors from "../../../../../../api/hooks/useColors";
import CustomToolTip from "../../../../../../components/CustomTooltip";
import Evidence from "../../../../../../components/Evidence";
import { Toast } from "../../../../../../components/Toast";
import SvgToIcon from "../../../../../../icons/SvgToIcon";
import {
  sanitizeSecrets,
  sanitizeXml,
} from "../../../../../../utils/sanitizeSecrets";
import { extractXmlContent } from "../../../../../../utils/xmlFormatter";
import { icons } from "../../../../../stack/environment/icons/iconExport";
import CustomLabel from "../../../../frameworks/components/CustomLabel";

const MisconfigurationEvidence = ({ controlId, evidence }) => {
  const [evidenceConfig, setEvidenceConfig] = useState("");
  const [evidenceConfigFormat, setEvidenceConfigFormat] = useState("json");
  const [recommendedConfig, setRecommendedConfig] = useState("");
  const [recommendedConfigFormat, setRecommendedConfigFormat] =
    useState("json");
  const colors = useColors();
  const { trackEvent } = useAPI();

  const CodeBlock = styled(Paper)(({ theme }) => ({
    padding: "16px",
    backgroundColor: colors.grey_8percent,
    whiteSpace: "pre-wrap",
    fontFamily: "Menlo",
    fontWeight: "400",
    borderRadius: "6px",
    fontSize: "12px",
    boxShadow: "none",
    border: `1px solid ${colors.divider2}`,
    maxHeight: "300px",
    overflow: "auto",
  }));

  useEffect(() => {
    if (evidence) {
      const parsedEvidence = JSON.parse(evidence);
      const evidenceConfigFormat = parsedEvidence.config_format || "json";
      setEvidenceConfigFormat(evidenceConfigFormat);
      const recommendedConfigFormat =
        parsedEvidence.recommended_config_format || "json";
      setRecommendedConfigFormat(recommendedConfigFormat);

      setEvidenceConfig(parsedEvidence?.config);
      setRecommendedConfig(parsedEvidence?.recommended_config);
    }
  }, [evidence]);

  const formatConfigData = (data) => {
    if (evidenceConfigFormat === "xml") {
      return sanitizeXml(extractXmlContent(data));
    }
    return JSON.stringify(sanitizeSecrets(data), null, 2);
  };

  const formatRecommendedConfigData = (data) => {
    if (recommendedConfigFormat === "xml") {
      return sanitizeXml(extractXmlContent(data));
    }
    return JSON.stringify(sanitizeSecrets(data), null, 2);
  };

  const handleCopy = (codeBlock, codeBlockName) => {
    trackEvent(`User copied ${codeBlockName} information`).then();
    navigator.clipboard.writeText(formatRecommendedConfigData(codeBlock));
    Toast.success(
      `${capitalize(codeBlockName)} information copied to clipboard!`
    );
  };

  const handleDownload = (codeBlock, fileName) => {
    trackEvent(`User downloaded ${fileName}`).then();
    const mimeType =
      recommendedConfigFormat === "xml"
        ? "application/xml"
        : "application/json";
    const extension = recommendedConfigFormat === "xml" ? ".xml" : ".json";
    const baseFileName = fileName.replace(/\.(txt|json|xml)$/i, "");

    const blob = new Blob([formatRecommendedConfigData(codeBlock)], {
      type: mimeType,
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `gap_${controlId}_${baseFileName}${extension}`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Box
        sx={{
          display: "block",
          marginBottom: "16px",
        }}
      >
        <CustomLabel
          colorFamily={"primary"}
          severity={"dark"}
          labelType="soft"
          size="medium"
          text="BETA"
        />
      </Box>

      <Box sx={{ marginBottom: "28px" }}>
        <Evidence
          evidence={formatConfigData(evidenceConfig)}
          fileNameToDownload={`gap_${controlId}_evidence.txt`}
        />
      </Box>

      {recommendedConfig && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginBottom: "8px",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <SvgToIcon
                IconLogo={icons.recommendedConfiguration}
                width={"20px"}
                style={{ marginRight: "8px", color: colors.common_white }}
              />
              <Typography variant="subtitle1">
                Recommended Configuration
              </Typography>
            </Box>
            <Box
              display={"flex"}
              gap={1}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  border: "1px solid " + colors.divider2,
                  borderRadius: "8px",
                  padding: "4px",
                  width: "28px",
                  height: "28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CustomToolTip title="Copy the recommended configuration">
                  <IconButton
                    aria-label="copy"
                    onClick={() =>
                      handleCopy(recommendedConfig, "recommended configuration")
                    }
                    size="small"
                  >
                    <ContentCopyRoundedIcon
                      sx={{ color: colors.common_black, fontSize: "16px" }}
                    />
                  </IconButton>
                </CustomToolTip>
              </Box>
              <Box
                sx={{
                  border: "1px solid " + colors.divider2,
                  borderRadius: "8px",
                  padding: "4px",
                  width: "28px",
                  height: "28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CustomToolTip title="Download the recommended configuration">
                  <IconButton
                    aria-label="download"
                    onClick={() =>
                      handleDownload(
                        recommendedConfig,
                        "recommended_configuration"
                      )
                    }
                    sx={{ color: colors.common_black, fontSize: "16px" }}
                    size="small"
                  >
                    <DownloadRoundedIcon
                      sx={{ color: colors.common_black, fontSize: "16px" }}
                    />
                  </IconButton>
                </CustomToolTip>
              </Box>
            </Box>
          </Box>
          <CodeBlock>
            {formatRecommendedConfigData(recommendedConfig)}
          </CodeBlock>
        </>
      )}
    </>
  );
};

export default MisconfigurationEvidence;
